import classNames from "classnames";
import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useEntities } from "@hooks/useEntities";
import { Button } from "@design-system/Button/Button";
import { TransactionCategoryRow } from "../viewUtils";
import styles from "../children/CategoryItem.module.scss";

export const TransactionsCell = memo(function TransactionsCell({
    row,
    table,
}: CellContext<ViewTableFeatures, TransactionCategoryRow, unknown>) {
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const tableMeta = table.options.meta as
        | { entityIds?: number[] }
        | undefined;
    const entities = useEntities();
    const entityIds = tableMeta?.entityIds?.length
        ? tableMeta.entityIds
        : entities.map((e) => e.id);

    const { id, transactionsCount, children } = row.original;

    return (
        <RowExpansion row={row}>
            {(isExpanded) => {
                // When collapsed (parent row, not expanded), show group count
                const countToDisplay =
                    !isExpanded && (children?.length ?? 0) > 0
                        ? row.original.groupTransactionsCount
                        : transactionsCount;

                const categoryIds = [
                    id,
                    ...(!isExpanded ? (children ?? []).map((c) => c.id) : []),
                ];

                if (countToDisplay <= 0) {
                    return (
                        <ViewTableCell>
                            <span
                                className={classNames(
                                    styles.label,
                                    styles.zero,
                                )}
                            >
                                {countToDisplay}
                            </span>
                        </ViewTableCell>
                    );
                }

                return (
                    <ViewTableCell captureClicks>
                        <Button
                            variant="ghost"
                            className={styles.transactionsButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                openGlobalTransactionViewDrawer({
                                    category: categoryIds,
                                    entityIds,
                                });
                            }}
                        >
                            {countToDisplay}
                        </Button>
                    </ViewTableCell>
                );
            }}
        </RowExpansion>
    );
});
